// src/components/Hero/Hero.jsx
import { useEffect } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
// import TextToSpeech from '../../TextToSpeech/textToSpeech'; // Kommentoitu pois
import PropTypes from "prop-types";
import ResponsiveButton from "../ResponsiveButton/ResponsiveButton";
import { trackEvent } from "../../utils/analytics";

// Images
import PreSonus from "../../assets/Presonus2000w.webp";

const heroChips = [
  "Äänentoisto",
  "Valotekniikka",
  "Laitevuokraus",
  "Kuljetus & asennus",
];

const Hero = ({ theme = "default-theme" }) => {
  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: "ease-out",
      once: true,
    });
    AOS.refresh();
  }, []);

  return (
<section
  className={`relative overflow-hidden pb-20 pt-28 sm:pt-32 bg-gradient-to-br from-white via-[#F3FFFC] to-[#E9FBF7] dark:from-[#050505] dark:via-[#08080A] dark:to-[#0D0D12] ${theme}`}
  aria-label="Spiderlegs Productions tapahtumatekniikan vuokraus"
>
      {/* Kevyt taustakorostus */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none absolute inset-0 -z-10
          bg-[radial-gradient(circle_at_top_left,rgba(24,214,190,0.18),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(109,40,217,0.14),transparent_32%)]
          dark:bg-[radial-gradient(circle_at_top_left,rgba(24,214,190,0.16),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(109,40,217,0.18),transparent_32%)]
        "
      />

      <div className="mx-4 mb-10 rounded-3xl p-4 sm:p-6 2xl:mx-72">
        <div
          className="
            grid grid-cols-1 place-items-center gap-8
            rounded-[2rem] border border-border/80 bg-card/95 p-5 shadow-xl
            dark:border-darkborder dark:bg-darkcard/95
            md:grid-cols-2 md:p-8
          "
        >
          {/* Kuva */}
          <div
            data-aos="fade-left"
            className="
              order-1 flex w-full justify-center
              md:order-2
            "
          >
            <div
              className="
                relative flex w-full max-w-[460px] justify-center
                rounded-[2rem] bg-surface2/80 p-4
                dark:bg-darksurface2
              "
            >
              <div
                aria-hidden="true"
                className="
                  absolute inset-6 rounded-full bg-primary/20 blur-3xl
                  dark:bg-primary/15
                "
              />

              {/* 2000W äänentoistopaketti kuva */}
              <img
                src={PreSonus}
                title="PreSonus 2000W äänentoistopaketti"
                alt="PreSonus 2000W äänentoistopaketti"
                className="
                  relative z-10 max-h-[420px] w-auto rotate-3 object-contain
                  drop-shadow-[0_24px_30px_rgba(0,0,0,0.35)]
                  transition-transform duration-500 ease-out
                  hover:scale-[1.03]
                "
              />
            </div>
          </div>

          {/* Tekstilaatikko */}
          <div
            data-aos="fade-right"
            className="order-2 w-full md:order-1"
            style={{ maxWidth: "100%" }}
          >
            {/* Iskulause / chip */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span
                className="
                  inline-flex items-center rounded-full border border-primary/40
                  bg-primary/10 px-4 py-2 font-customFont1 text-sm font-bold
                  uppercase tracking-[0.16em] text-text
                  dark:border-primary/35 dark:bg-primary/10 dark:text-darktext
                "
              >
                Vaivaton
              </span>

              <span
                className="
                  inline-flex items-center rounded-full border border-border
                  bg-surface px-4 py-2 font-customFont1 text-sm font-medium
                  text-muted
                  dark:border-darkborder dark:bg-darksurface dark:text-darkmuted
                "
              >
                Keravalta Etelä-Suomeen
              </span>
            </div>

            {/* Otsikko */}
            <h1
              className="
                font-customFont2 text-left text-3xl sm:text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-snug
                text-text
                dark:text-darktext
              "
            >
              Tapahtumatekniikan vuokraus
            </h1>

            {/* Teksti */}
            <p
              className="
                mt-5 max-w-2xl font-customFont1 text-left text-lg font-normal
                leading-relaxed text-muted
                dark:text-darkmuted
                sm:text-xl xl:text-2xl
              "
            >
              Äänentoisto, esitystekniikka ja muut vuokrattavat laitteet
              juhliin, yritystapahtumiin ja yksityistilaisuuksiin – helposti
              yhdestä paikasta.
            </p>

            {/* Chipit */}
            <div className="mt-6 flex flex-wrap gap-2">
              {heroChips.map((chip) => (
                <span
                  key={chip}
                  className="
                    inline-flex rounded-full border border-border bg-surface2
                    px-4 py-2 font-customFont1 text-sm font-semibold text-text
                    dark:border-darkborder dark:bg-darksurface2 dark:text-darktext
                  "
                >
                  {chip}
                </span>
              ))}
            </div>

            <div className="hero-button mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/vuokrattavat"
                onClick={() => {
                  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
                }}
              >
                <ResponsiveButton
                  variant="hero"
                  onClick={() => {
                    trackEvent("hero_katso_vuokrattavat_click", {
                      sijainti: "etusivu_hero",
                    });
                  }}
                >
                  Katso vuokrattavat
                </ResponsiveButton>
              </Link>

          <Link
              to="/tee-varaus"
              onClick={() => {
                trackEvent("hero_tee_varaus_click", {
                  sijainti: "etusivu_hero",
                });
                window.scrollTo({ top: 0, left: 0, behavior: "auto" });
              }}
              className="
                inline-flex min-h-[50px] items-center justify-center rounded-full
                border border-[#18D6BE] bg-white px-7 py-3 font-customFont1
                text-base font-bold !text-[#111827] shadow-md transition
                hover:-translate-y-0.5 hover:bg-[#18D6BE] hover:!text-[#111827] hover:shadow-lg
                dark:border-[#18D6BE]/60 dark:bg-[#111114] dark:!text-white
                dark:hover:bg-[#18D6BE] dark:hover:!text-[#111827]
              "
            >
              Tee varaus
            </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

Hero.propTypes = {
  theme: PropTypes.string,
};

export default Hero;